import { useCallback, useEffect, useState } from "react";
import { SubClassesObject } from "../../../../../services/decisionFlow";
import { CheckableValue } from "../../../../../components/checkable-list";
import { setQuery } from "../../../../../services/local-session-storage";
import { SubClassType } from "../../index";

export type CheckableSubclass = {
    active: string;
    checkableSubClass: CheckableValue<SubClassesObject>[];
};

/** Estado e seleção da etapa de subclasses do wizard. */
export function useSubClass(subclass: SubClassType[], onFinish: () => void) {
    const [subClassType, setSubClassType] = useState<CheckableSubclass[]>([]);

    const transformSubclass = useCallback((toTransform: SubClassType[]) => {
        const transformedList = [...toTransform]
            .sort((a, b) => a.active.localeCompare(b.active))
            .map((value) => {
                const checkable = [...value.subClass]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(
                        (sub) =>
                            ({
                                value: sub,
                                isSelected: false,
                            } as CheckableValue<SubClassesObject>)
                    );

                return {
                    active: value.active,
                    checkableSubClass: checkable,
                } as CheckableSubclass;
            });
        setSubClassType(transformedList);
    }, []);

    useEffect(() => {
        transformSubclass(subclass);
    }, [transformSubclass, subclass]);

    const getSelectedItems = useCallback(
        () => subClassType.flatMap((list) => list.checkableSubClass.filter((check) => check.isSelected).map((value) => value.value)),
        [subClassType]
    );

    const handleContinue = () => {
        setQuery({ subclass: getSelectedItems() });
        onFinish();
    };

    const handleOnChange = useCallback(
        (value: SubClassesObject, isSelected: boolean) => {
            const newList = [...subClassType];
            for (let i = 0; i < newList.length; i++) {
                for (let j = 0; j < newList[i].checkableSubClass.length; j++) {
                    const checkableSubClass = newList[i].checkableSubClass[j];
                    if (checkableSubClass.value.id === value.id) {
                        checkableSubClass.isSelected = isSelected;
                        break;
                    }
                }
            }
            setSubClassType(newList);
        },
        [subClassType]
    );

    const clearAll = useCallback(() => {
        setSubClassType((prev) =>
            prev.map((list) => ({
                ...list,
                checkableSubClass: list.checkableSubClass.map((check) => (check.isSelected ? { ...check, isSelected: false } : check)),
            }))
        );
    }, []);

    const selectedCount = getSelectedItems().length;

    return {
        subClassType,
        selectedCount,
        handleContinue,
        handleOnChange,
        clearAll,
    };
}
